/**
 * TabGroup Component
 *
 * Accessible tab navigation with keyboard support:
 * - Left/Right arrow key navigation
 * - ARIA tablist/tab/tabpanel roles
 * - Optional count badge on tabs
 * - Active tab indicator
 *
 * SCRUM-904: TabGroup Component Extraction
 *
 * @component
 * @param {Object} props
 * @param {Array<{id: string, label: string, count?: number}>} props.tabs - Tab definitions
 * @param {string} props.activeTab - Currently active tab ID
 * @param {Function} props.onChange - Callback when tab changes (receives tab ID)
 * @param {string} [props.className] - Additional classes for the tab list container
 */
import { useRef, useCallback } from 'react';
import PropTypes from 'prop-types';

export default function TabGroup({ tabs, activeTab, onChange, className = '' }) {
  const tabRefs = useRef({});

  const handleKeyDown = useCallback(
    (e) => {
      const currentIndex = tabs.findIndex((t) => t.id === activeTab);
      let nextIndex;

      if (e.key === 'ArrowRight') {
        e.preventDefault();
        nextIndex = (currentIndex + 1) % tabs.length;
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      } else if (e.key === 'Home') {
        e.preventDefault();
        nextIndex = 0;
      } else if (e.key === 'End') {
        e.preventDefault();
        nextIndex = tabs.length - 1;
      } else {
        return;
      }

      const nextTab = tabs[nextIndex];
      onChange(nextTab.id);
      tabRefs.current[nextTab.id]?.focus();
    },
    [tabs, activeTab, onChange]
  );

  return (
    <div
      role="tablist"
      aria-orientation="horizontal"
      className={`flex border-b border-gray-200 ${className}`.trim()}
      onKeyDown={handleKeyDown}
    >
      {tabs.map((tab) => {
        const isActive = tab.id === activeTab;

        return (
          <button
            key={tab.id}
            ref={(el) => { tabRefs.current[tab.id] = el; }}
            role="tab"
            id={`tab-${tab.id}`}
            aria-selected={isActive}
            aria-controls={`tabpanel-${tab.id}`}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              isActive
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {tab.label}
            {tab.count !== undefined && tab.count !== null && (
              <span
                className={`ml-2 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  isActive
                    ? 'bg-blue-100 text-blue-600'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

TabGroup.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      count: PropTypes.number,
    })
  ).isRequired,
  activeTab: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string,
};
